import { BADGE_PALETTES, ICE_ENAMEL } from './palettes.js'
import { BADGE_SHAPES } from './shapes.js'
import { BADGE_TIER_STYLES } from './tiers.js'
import {
    BadgeArtColors,
    BadgeArtRenderOptions,
    BadgeArtSpec,
    BadgeEnamel,
    BadgeShape,
    ICON_FILL_TOKEN,
} from './types.js'

const f = (n: number): number => Number(n.toFixed(2))
const VB = 'viewBox="-14 -12 228 236"'
const REG =
    'filterUnits="userSpaceOnUse" x="-14" y="-12" width="228" height="236" color-interpolation-filters="sRGB"'
const WIDE =
    'filterUnits="userSpaceOnUse" x="-60" y="-60" width="320" height="330" color-interpolation-filters="sRGB"'
const LX = 30
const LY = 4
const FONT = "'Hanken Grotesk', Arial, Helvetica, sans-serif"

/** `var(--x)` only works inside style=""; literals work as attributes (and in resvg). */
const fillAttr = (p: string): string => (p.startsWith('var(') ? `style="fill:${p}"` : `fill="${p}"`)
const strokeAttr = (p: string): string =>
    p.startsWith('var(') ? `style="stroke:${p}"` : `stroke="${p}"`

interface Resolved {
    S: BadgeShape
    C: BadgeEnamel
    ramp: [number, number, number][]
    ins: [number, number, number]
    iconMarkup: string | null
    num: number | null
}

const resolveSpec = (spec: BadgeArtSpec): Resolved => {
    const S = BADGE_SHAPES[spec.shape]
    const palette = BADGE_PALETTES[spec.palette]
    const C: BadgeEnamel =
        spec.tier === 'crystal' ? ICE_ENAMEL : { ring: palette.ring, field: palette.field }
    const isYear = spec.anniversaryYears !== null
    return {
        S,
        C,
        ramp: BADGE_TIER_STYLES[spec.tier].ramp,
        ins: S.inset,
        iconMarkup: isYear ? null : spec.iconMarkup,
        num: isYear ? Math.max(1, Math.round(spec.anniversaryYears!)) : null,
    }
}

const numSpec = (num: number) => {
    const two = String(num).length > 1
    return { fs: two ? 62 : 80, cy: 101, ls: two ? -3 : 0 }
}

/** Year arcs around the ring medal: one arc per year. */
const annExtra = (b: Resolved, paint: string): string => {
    if (b.num === null) return ''
    const R = 77
    const N = b.num
    const st = 360 / N
    const g = Math.min(6, 90 / N)
    const w = N > 16 ? 6 : 8
    let s = ''
    for (let i = 0; i < N; i++) {
        const a0 = ((-90 + i * st + g) * Math.PI) / 180
        const a1 = ((-90 + (i + 1) * st - g) * Math.PI) / 180
        const large = st - 2 * g > 180 ? 1 : 0
        s += `<path d="M${f(100 + R * Math.cos(a0))} ${f(100 + R * Math.sin(a0))}A${R} ${R} 0 ${large} 1 ${f(100 + R * Math.cos(a1))} ${f(100 + R * Math.sin(a1))}" fill="none" ${strokeAttr(paint)} stroke-width="${w}" stroke-linecap="round"/>`
    }
    return s
}

const iconMarkup = (b: Resolved, paint: string, target: number): string => {
    const [cx, cy, k] = b.S.icon
    if (b.num !== null) {
        const n = numSpec(b.num)
        return `<text x="${f(100 + n.ls / 2)}" y="${f(n.cy + n.fs * 0.36)}" text-anchor="middle" font-family="${FONT}" font-weight="800" font-size="${n.fs}" letter-spacing="${n.ls}" ${fillAttr(paint)} stroke="none">${b.num}</text>`
    }
    if (!b.iconMarkup) return ''
    const tx = cx - 32 * k
    const ty = cy - 32 * k
    const w = Math.min(9, Math.max(5, target / k))
    const inner = b.iconMarkup.split(ICON_FILL_TOKEN).join(fillAttr(paint))
    return `<g transform="translate(${f(tx)} ${f(ty)}) scale(${k})" fill="none" ${strokeAttr(paint)} stroke-width="${f(w)}" stroke-linecap="round" stroke-linejoin="round">${inner}</g>`
}

const shell = (id: string, b: Resolved, extraDefs: string): string =>
    `<svg xmlns="http://www.w3.org/2000/svg" ${VB} aria-hidden="true" focusable="false"><defs><path id="s${id}" d="${b.S.d}"/><clipPath id="c${id}"><use href="#s${id}"/></clipPath>${extraDefs}</defs>`

const ringMask = (id: string, w: number): string =>
    `<mask id="k${id}" maskUnits="userSpaceOnUse" x="-14" y="-12" width="228" height="236"><rect x="-14" y="-12" width="228" height="236" fill="#fff"/><use href="#s${id}" fill="none" stroke="#000" stroke-width="${w}"/></mask>`

/** Rim, inner line, icon (or numerals unless `noText`) and year arcs in one paint. */
const metalParts = (id: string, b: Resolved, paint: string, noText = false): string =>
    `<use href="#s${id}" fill="none" stroke="${paint}" stroke-width="${b.ins[0]}" clip-path="url(#c${id})"/>` +
    (b.S.noLine
        ? ''
        : `<use href="#s${id}" fill="none" stroke="${paint}" stroke-width="${b.ins[2]}" clip-path="url(#c${id})" mask="url(#k${id})"/>`) +
    (noText && b.num !== null ? '' : iconMarkup(b, paint, 7.4)) +
    annExtra(b, paint)

/** Locked (not yet activated) badge: debossed slot in the page surface. */
const slotSvg = (id: string, b: Resolved, pal: BadgeArtColors): string => {
    const defs = `<filter id="in${id}" ${REG}><feFlood style="flood-color:${pal.slotShade}" result="fl"/><feComposite in="fl" in2="SourceAlpha" operator="out" result="out"/><feOffset in="out" dx="3" dy="7" result="o1"/><feGaussianBlur in="o1" stdDeviation="5" result="b1"/><feComposite in="b1" in2="SourceAlpha" operator="in" result="sh"/><feFlood style="flood-color:${pal.slotHi}" result="fh"/><feComposite in="fh" in2="SourceAlpha" operator="out" result="oh"/><feOffset in="oh" dx="-2" dy="-3" result="o2"/><feGaussianBlur in="o2" stdDeviation="1.6" result="b2"/><feComposite in="b2" in2="SourceAlpha" operator="in" result="hi"/><feMerge><feMergeNode in="SourceGraphic"/><feMergeNode in="hi"/><feMergeNode in="sh"/></feMerge></filter>`
    return (
        shell(id, b, defs) +
        `<use href="#s${id}" style="fill:${pal.slot}" filter="url(#in${id})"/>` +
        `<g transform="translate(0 2)">${iconMarkup(b, pal.slotHi, 7.2)}</g>${iconMarkup(b, pal.slotInk, 7.2)}</svg>`
    )
}

/** Grey-scale environment strip the metal "reflects"; displaced by the surface normals. */
const ENV = (() => {
    const st: [number, number][] = [
        [0, 0.5],
        [0.1, 0.97],
        [0.25, 0.9],
        [0.31, 0.32],
        [0.42, 0.24],
        [0.47, 0.86],
        [0.52, 0.8],
        [0.58, 0.16],
        [0.72, 0.1],
        [0.86, 0.46],
        [1, 0.64],
    ]
    const stops = st
        .map(([o, v]) => {
            const c = Math.round(v * 255)
            return `<stop offset='${o}' stop-color='rgb(${c},${c},${c})'/>`
        })
        .join('')
    return (
        'data:image/svg+xml,' +
        encodeURIComponent(
            `<svg xmlns='http://www.w3.org/2000/svg' width='330' height='340' viewBox='0 0 330 340' preserveAspectRatio='none'><defs><linearGradient id='e' x1='0' y1='0' x2='.28' y2='1'>${stops}</linearGradient></defs><rect width='330' height='340' fill='url(#e)'/></svg>`,
        )
    )
})()

const toHex = (p: [number, number, number]): string =>
    '#' +
    p
        .map((v) =>
            Math.round(v * 255)
                .toString(16)
                .padStart(2, '0'),
        )
        .join('')

const envFilter = (id: string, ramp: [number, number, number][]): string => {
    const tab = (i: number) => ramp.map((p) => p[i]).join(' ')
    return `<filter id="${id}" ${WIDE}><feGaussianBlur in="SourceAlpha" stdDeviation="2.4" result="h"/><feColorMatrix in="h" type="matrix" values="0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 0 1" result="hg"/><feConvolveMatrix in="hg" order="3" kernelMatrix="-1 0 1 -2 0 2 -1 0 1" divisor="3" bias=".5" preserveAlpha="true" edgeMode="duplicate" result="nx"/><feConvolveMatrix in="hg" order="3" kernelMatrix="-1 -2 -1 0 0 0 1 2 1" divisor="3" bias=".5" preserveAlpha="true" edgeMode="duplicate" result="ny"/><feColorMatrix in="nx" type="matrix" values="1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1" result="rx"/><feColorMatrix in="ny" type="matrix" values="0 0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 0 1" result="gy"/><feComposite in="rx" in2="gy" operator="arithmetic" k2="1" k3="1" result="n"/><feImage href="${ENV}" x="-50" y="-52" width="330" height="340" preserveAspectRatio="none" result="e"/><feDisplacementMap in="e" in2="n" scale="64" xChannelSelector="R" yChannelSelector="G" result="r"/><feComponentTransfer in="r" result="t"><feFuncR type="table" tableValues="${tab(0)}"/><feFuncG type="table" tableValues="${tab(1)}"/><feFuncB type="table" tableValues="${tab(2)}"/></feComponentTransfer><feComposite in="t" in2="SourceAlpha" operator="in" result="m"/><feSpecularLighting in="h" surfaceScale="3" specularConstant=".8" specularExponent="70" lighting-color="#fff" result="sl"><fePointLight x="${LX}" y="${LY}" z="150"/></feSpecularLighting><feComposite in="sl" in2="SourceAlpha" operator="in" result="sp"/><feComposite in="m" in2="sp" operator="arithmetic" k2="1" k3="1"/></filter>`
}

/** Activated badge: enamel face, environment-mapped metal rim, line, icon and arcs. */
const activeSvg = (id: string, b: Resolved, pal: BadgeArtColors): string => {
    const R = b.ramp
    const defs =
        `<linearGradient id="sd${id}" gradientUnits="userSpaceOnUse" x1="0" y1="40" x2="0" y2="215"><stop offset="0" stop-color="${toHex(R[0])}"/><stop offset=".5" stop-color="${toHex(R[1])}"/><stop offset="1" stop-color="${toHex(R[0])}"/></linearGradient>` +
        ringMask(id, b.ins[1]) +
        envFilter('env' + id, R) +
        `<linearGradient id="eg${id}" x1="0" y1="0" x2=".3" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".26"/><stop offset=".5" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".14"/></linearGradient>` +
        `<filter id="mg${id}" ${REG}><feGaussianBlur stdDeviation="3.4"/></filter>` +
        `<filter id="d${id}" ${REG}><feDropShadow dx="0" dy="2.6" stdDeviation="1.6" flood-color="#1a0c06" flood-opacity=".62"/></filter>` +
        `<filter id="ca${id}" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="2.6"/></filter>` +
        `<filter id="cb${id}" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="11"/></filter>`
    return (
        shell(id, b, defs) +
        `<use href="#s${id}" transform="translate(0 17)" style="fill:${pal.shadow}" opacity=".75" filter="url(#cb${id})"/>` +
        `<use href="#s${id}" transform="translate(0 6)" style="fill:${pal.shadow}" filter="url(#ca${id})"/>` +
        `<use href="#s${id}" transform="translate(0 4.5)" fill="url(#sd${id})"/>` +
        `<use href="#s${id}" fill="${b.C.field}"/>` +
        `<use href="#s${id}" fill="none" stroke="${b.C.ring}" stroke-width="${b.ins[1]}" clip-path="url(#c${id})"/>` +
        `<use href="#s${id}" fill="url(#eg${id})"/>` +
        `<g clip-path="url(#c${id})" style="mix-blend-mode:soft-light" opacity=".95"><g filter="url(#mg${id})">${metalParts(id, b, '#FFFFFF')}</g></g>` +
        `<g filter="url(#d${id})"><g filter="url(#env${id})">${metalParts(id, b, '#000000')}</g></g></svg>`
    )
}

/**
 * Pure string renderer shared by the client (inline SVG, CSS-variable colours) and the
 * server (resvg → PNG, literal colours). Deterministic: same input, same output.
 */
export const renderBadgeSvg = (spec: BadgeArtSpec, opts: BadgeArtRenderOptions): string => {
    const b = resolveSpec(spec)
    const id = opts.idPrefix
    return opts.state === 'slot' ? slotSvg(id, b, opts.colors) : activeSvg(id, b, opts.colors)
}

/** The three masks the WebGL engine bakes its textures from (200×200 grid, `size` px). */
export interface BadgeArtMasks {
    /** Badge silhouette, white on transparent. */
    shape: string
    /** Metal parts (rim, line, icon, arcs), white on transparent; numerals are left out
     *  so the engine can draw them on canvas with the page font. */
    metal: string
    /** Enamel field + ring in their colours. */
    enamel: string
    /** Anniversary years, or null. */
    num: number | null
    /** Outline path on the 200 grid (for the extrusion). */
    d: string
    /** [cx, cy, scale] of the icon area (crystal dome apex). */
    iconCenter: [number, number, number]
}

const maskDoc = (inner: string, size: number): string =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="${size}" height="${size}">${inner}</svg>`

export const renderBadgeMasks = (spec: BadgeArtSpec, size: number): BadgeArtMasks => {
    const b = resolveSpec(spec)
    const defs = `<defs><path id="sm" d="${b.S.d}"/><clipPath id="cm"><use href="#sm"/></clipPath>${ringMask('m', b.ins[1])}</defs>`
    return {
        shape: maskDoc(defs + `<use href="#sm" fill="#fff"/>`, size),
        metal: maskDoc(defs + metalParts('m', b, '#FFFFFF', true), size),
        enamel: maskDoc(
            defs +
                `<use href="#sm" fill="${b.C.field}"/><use href="#sm" fill="none" stroke="${b.C.ring}" stroke-width="${b.ins[1]}" clip-path="url(#cm)"/>`,
            size,
        ),
        num: b.num,
        d: b.S.d,
        iconCenter: b.S.icon,
    }
}

/** Numeral layout for anniversary badges (shared by the SVG text and the canvas numerals). */
export const badgeNumeralSpec = (num: number) => numSpec(num)
